import { TEAM_IDS, teamsOf } from 'src/contexts/identityAndTenancy';
import TeamMembershipsDB from 'src/fb/teamMemberships-db';
import { computed, ref } from 'vue';

/** One document per user per clinic, keyed by both, as the rules require. */
const membershipIdFor = (clinic, uid) => `${clinic}_${uid}`;

/**
 * A user's teams in the user editor: what was saved, or their role's default while nothing was.
 *
 * Identity & Tenancy owns the rule; reading and saving live here because that context's published
 * interface may not reach the database layer, which imports the store beneath it.
 *
 * WHILE NOTHING WAS SAVED, THE TEAMS FOLLOW THE ROLE, so changing somebody's role in the editor moves
 * their default teams with it. Once anybody ticks a box, the choice is theirs and is saved as made.
 *
 * @param {{clinic: () => string|null, uid: () => string|null, role: () => string|null}} subject
 */
// eslint-disable-next-line import/prefer-default-export
export function useUserTeams({ clinic, uid, role }) {
  const saved = ref(null);
  const chosen = ref(null);
  const loaded = ref(false);

  const teams = computed({
    get: () => chosen.value ?? teamsOf(saved.value, role()),
    set: (value) => { chosen.value = value.filter((team) => TEAM_IDS.includes(team)); },
  });

  async function load() {
    loaded.value = false;
    chosen.value = null;
    saved.value = null;
    if (clinic() && uid()) {
      const document = await new TeamMembershipsDB().read(membershipIdFor(clinic(), uid()));

      saved.value = Array.isArray(document?.teams) ? { teams: document.teams } : null;
    }
    loaded.value = true;
  } // load()

  /** Saves only a choice somebody made; a default that nobody touched stays a default. */
  async function save() {
    if (chosen.value === null || !clinic() || !uid()) {
      return;
    }
    await new TeamMembershipsDB().set(
      { clinic: clinic(), user: uid(), teams: chosen.value },
      membershipIdFor(clinic(), uid()),
    );
    saved.value = { teams: chosen.value };
    chosen.value = null;
  } // save()

  return {
    teams, loaded, load, save,
  };
} // useUserTeams()
